import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase/client';
import { Loader2 } from 'lucide-react';

export function AuthCallback() {
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Combine hash and search parameters:
        const hash = window.location.hash;
        const search = window.location.search;
        // Parse hash parameters (if any)
        const hashParams = new URLSearchParams(hash.replace('#', '?'));
        // Parse search parameters
        const searchParams = new URLSearchParams(search);
        
        // Prefer parameters from the hash, fallback to search parameters.
        const type = hashParams.get('type') || searchParams.get('type');
        const token = hashParams.get('token') || searchParams.get('token');

        // Handle password recovery flow
        if (type === 'recovery' && token) {
          // Optionally, store the token if needed by your reset password page
          sessionStorage.setItem('resetToken', token);
          navigate('/auth/reset-password');
          return;
        }

        // For other auth flows, exchange code for session
        const { data, error } = await supabase.auth.exchangeCodeForSession(
          window.location.href
        );

        if (error) {
          throw new Error(error.message || 'Authentication failed. Please try signing in again.');
        }

        if (!data?.session) {
          throw new Error('No session data found. Please try signing in again.');
        }

        // Success notification
        toast({
          title: 'Success',
          description: `Welcome back, ${data.session.user.email}!`,
        });

        // Redirect to home page
        navigate('/', { replace: true });
      } catch (err) {
        console.error('Auth callback error:', err);

        // Handle error cases with a descriptive message
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'An unexpected error occurred during authentication.';

        // Notify the user of the error
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });

        // Redirect the user to the login page for a retry
        navigate('/auth', { replace: true });
      }
    };

    handleCallback();
  }, [navigate, toast]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen">
      <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" aria-hidden="true" />
      <p className="text-muted-foreground" aria-live="polite">
        Completing sign in...
      </p>
    </div>
  );
}
